import React, { useEffect, useState } from 'react';
import { XIcon, CodeIcon, CopyIcon, CheckIcon } from 'lucide-react';
import { usePreventScroll } from '../../../hooks/usePreventScroll';
import { toast } from 'sonner';
import { ERROR_MESSAGES } from '../../../config/error-messages';
import { useErrorBoundary } from '../../../hooks/useErrorBoundary';
interface Props {
  isOpen: boolean;
  onClose: () => void;
  chatbot: {
    id: string;
    name: string;
  };
}
export const ChatbotEmbedModal = ({
  isOpen,
  onClose,
  chatbot
}: Props) => {
  const [{
    hasError,
    error
  }, handleError, resetError] = useErrorBoundary();
  const [copiedCode, setCopiedCode] = useState(false);
  const [copiedScript, setCopiedScript] = useState(false);
  // Prevent scroll on body when modal is open
  usePreventScroll(isOpen);
  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);
  const [copied, setCopied] = useState(false);
  const [selectedTheme, setSelectedTheme] = useState<'light' | 'dark'>('dark');
  const [selectedPosition, setSelectedPosition] = useState<'bottom-right' | 'bottom-left'>('bottom-right');
  const embedCode = `<script>
  window.chatbotConfig = {
    id: "${chatbot.id}",
    theme: "${selectedTheme}",
    position: "${selectedPosition}",
    name: "${chatbot.name}"
  };
</script>
<script src="https://cdn.knowledge-space.ai/chatbot.js"></script>`;
  const handleCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success('Code copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      handleError(error as Error);
      toast.error('Failed to copy code to clipboard');
    }
  };
  if (hasError) {
    return <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-id="element-747">
        <div className="bg-surface-light border border-surface-border rounded-none p-6 max-w-lg w-full" data-id="element-748">
          <div className="flex items-start gap-4" data-id="element-749">
            <div className="p-3 bg-status-error/10 rounded-none" data-id="element-750">
              <AlertCircleIcon size={24} className="text-status-error" data-id="element-751" />
            </div>
            <div className="flex-1" data-id="element-752">
              <h1 className="text-lg font-medium text-white font-mono mb-2" data-id="element-753">
                Error Generating Embed Code
              </h1>
              <div className="bg-surface p-4 rounded-none border border-surface-border mb-4" data-id="element-754">
                <code className="text-sm text-gray-400 font-mono" data-id="element-755">
                  {error?.message || ERROR_MESSAGES.api.serverError}
                </code>
              </div>
              <div className="flex gap-3" data-id="element-756">
                <button onClick={resetError} className="flex items-center gap-2 px-4 py-2 bg-accent 
                           hover:bg-accent-light text-white rounded-none transition-colors" data-id="element-757">
                  <RefreshCwIcon size={16} data-id="element-758" />
                  <span data-id="element-759">Try Again</span>
                </button>
                <button onClick={onClose} className="px-4 py-2 border border-surface-border text-gray-400 
                           hover:text-white rounded-none transition-colors" data-id="element-760">
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>;
  }
  if (!isOpen) return null;
  return <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-id="element-761">
      <div className="bg-surface-light border border-surface-border rounded-none w-full max-w-2xl" data-id="element-762">
        <div className="px-6 py-4 border-b border-surface-border flex items-center justify-between" data-id="element-763">
          <div className="flex items-center gap-3" data-id="element-764">
            <CodeIcon size={20} className="text-accent-light" data-id="element-765" />
            <h2 className="text-lg font-medium text-white font-mono" data-id="element-766">
              Embed {chatbot.name}
            </h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-300 transition-colors" data-id="element-767">
            <XIcon size={20} data-id="element-768" />
          </button>
        </div>
        <div className="p-6 space-y-6" data-id="element-769">
          <div className="space-y-4" data-id="element-770">
            <h3 className="text-sm font-medium text-white font-mono" data-id="element-771">
              Appearance
            </h3>
            <div className="grid grid-cols-2 gap-4" data-id="element-772">
              <div data-id="element-773">
                <label className="block text-sm text-gray-400 mb-2" data-id="element-774">
                  Theme
                </label>
                <div className="flex gap-3" data-id="element-775">
                  <button onClick={() => setSelectedTheme('light')} className={`
                      px-4 py-2 border rounded-none font-mono text-sm
                      ${selectedTheme === 'light' ? 'bg-accent text-white border-accent' : 'border-surface-border text-gray-400 hover:text-white'}
                    `} data-id="element-776">
                    Light
                  </button>
                  <button onClick={() => setSelectedTheme('dark')} className={`
                      px-4 py-2 border rounded-none font-mono text-sm
                      ${selectedTheme === 'dark' ? 'bg-accent text-white border-accent' : 'border-surface-border text-gray-400 hover:text-white'}
                    `} data-id="element-777">
                    Dark
                  </button>
                </div>
              </div>
              <div data-id="element-778">
                <label className="block text-sm text-gray-400 mb-2" data-id="element-779">
                  Position
                </label>
                <div className="flex gap-3" data-id="element-780">
                  <button onClick={() => setSelectedPosition('bottom-left')} className={`
                      px-4 py-2 border rounded-none font-mono text-sm
                      ${selectedPosition === 'bottom-left' ? 'bg-accent text-white border-accent' : 'border-surface-border text-gray-400 hover:text-white'}
                    `} data-id="element-781">
                    Bottom Left
                  </button>
                  <button onClick={() => setSelectedPosition('bottom-right')} className={`
                      px-4 py-2 border rounded-none font-mono text-sm
                      ${selectedPosition === 'bottom-right' ? 'bg-accent text-white border-accent' : 'border-surface-border text-gray-400 hover:text-white'}
                    `} data-id="element-782">
                    Bottom Right
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="space-y-2" data-id="element-783">
            <div className="flex items-center justify-between" data-id="element-784">
              <h3 className="text-sm font-medium text-white font-mono" data-id="element-785">
                Embed Code
              </h3>
              <button onClick={handleCopy} className="flex items-center gap-2 px-3 py-1 text-sm text-accent-light hover:text-accent transition-colors font-mono" data-id="element-786">
                {copied ? <>
                    <CheckIcon size={16} data-id="element-787" />
                    Copied!
                  </> : <>
                    <CopyIcon size={16} data-id="element-788" />
                    Copy Code
                  </>}
              </button>
            </div>
            <div className="bg-surface border border-surface-border rounded-none p-4" data-id="element-789">
              <pre className="text-sm text-gray-300 font-mono whitespace-pre-wrap" data-id="element-790">
                {embedCode}
              </pre>
            </div>
          </div>
          <div className="bg-surface p-4 border-l-2 border-accent" data-id="element-791">
            <h4 className="text-sm font-medium text-white font-mono mb-2" data-id="element-792">
              Implementation Notes
            </h4>
            <ul className="space-y-2 text-sm text-gray-400" data-id="element-793">
              <li data-id="element-794">• Add the code just before the closing {'</body>'} tag</li>
              <li data-id="element-795">• The chatbot will automatically initialize on page load</li>
              <li data-id="element-796">
                • The script is lightweight (~50kb) and loads asynchronously
              </li>
              <li data-id="element-797">
                • Customize appearance and behavior through the config object
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>;
};